"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { MapPin, Phone, Mail, Loader, ArrowRight, Building2, ShieldCheck, Zap } from "lucide-react";
const inputClasses = "flex h-11 w-full rounded-md border border-white/10 bg-white/5 px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:border-primary disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-300";
const textareaClasses = "flex min-h-[120px] w-full rounded-md border border-white/10 bg-white/5 px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:border-primary disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-300 resize-y";
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center animate-in fade-in zoom-in duration-500">
        <div className="mb-6 flex size-16 items-center justify-center rounded-md bg-primary/20 text-primary">
          <ShieldCheck className="size-8" />
        </div>
        <h3 className="font-default mb-2 text-2xl font-semibold text-foreground">
          Inquiry Received
        </h3>
        <p className="mb-8 max-w-md text-muted-foreground">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="rounded-md px-8 border-primary text-primary hover:bg-primary/10">
          Submit Another Inquiry
        </Button>
      </div>;
  }
  const interestOptions = ["Cybersecurity", "GRC & Compliance", "SOC Services", "Digital Transformation", "AI Governance"];
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid gap-5 md:grid-cols-2">
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/90 font-default">Full Name</FormLabel>
              <FormControl>
                <input {...field} type="text" className={inputClasses} placeholder="e.g. Ahmed Al Mansouri" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/90 font-default">Corporate Email</FormLabel>
              <FormControl>
                <input {...field} type="email" className={inputClasses} placeholder="name@organization.ae" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid gap-5 md:grid-cols-2">
        <FormField control={control} name="organization" rules={{
        required: "Organization name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/90 font-default">Organization</FormLabel>
              <FormControl>
                <input {...field} type="text" className={inputClasses} placeholder="Government or Enterprise Name" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="role" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/90 font-default">Industry</FormLabel>
              <FormControl>
                <input {...field} type="text" className={inputClasses} placeholder="e.g. Banking, Education, SME" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="phone" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/90 font-default">Contact Number</FormLabel>
            <FormControl>
              <input {...field} type="tel" className={inputClasses} placeholder="+971 50 000 0000" />
            </FormControl>
            <FormDescription className="text-xs text-muted-foreground/60">Optional for direct consultation scheduling.</FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="partnership_type" render={({
      field
    }) => <FormItem className="space-y-4 pt-2">
            <FormLabel className="text-foreground/90 font-default">Area of Interest</FormLabel>
            <FormControl>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {interestOptions.map((opt, i) => {
            const isChecked = field.value?.includes(opt);
            return <label key={opt} data-index={i} className={`flex cursor-pointer items-center gap-3 rounded-md border p-3 transition-all duration-200 ${isChecked ? "border-primary bg-primary/10 text-primary" : "border-white/10 bg-white/5 text-muted-foreground hover:border-white/20 hover:bg-white/10"}`}>
                      <input type="checkbox" className="size-4 rounded border-white/20 bg-transparent text-primary ring-offset-background focus:ring-2 focus:ring-primary focus:ring-offset-2" checked={isChecked} onChange={e => {
                const currentValues = field.value || [];
                if (e.target.checked) {
                  field.onChange([...currentValues, opt]);
                } else {
                  field.onChange(currentValues.filter((v: string) => v !== opt));
                }
              }} />
                      <span className="text-sm font-medium">{opt}</span>
                    </label>;
          })}
              </div>
            </FormControl>
            <FormDescription className="text-xs text-muted-foreground/60">Select the services relevant to your inquiry.</FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Please provide some details about your inquiry"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/90 font-default">Consultation Details</FormLabel>
            <FormControl>
              <textarea {...field} className={textareaClasses} placeholder="Briefly describe your technical requirements or project scope..." />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="rounded-md border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full rounded-md py-6 text-base font-semibold bg-primary text-primary-foreground shadow-lg shadow-primary/10 transition-all hover:bg-primary/90">
        {isSubmitting ? <>
            <Loader className="mr-2 size-5 animate-spin" />
            Sending Request...
          </> : <>
            Request a Consultation
            <ArrowRight className="ml-2 size-5" />
          </>}
      </Button>
    </Form>;
}
export default function ContactInquiry() {
  const discussionAreas = [{
    title: "Government & Enterprise Security",
    description: "cybersecurity governance, compliance alignment and resilient technology environments"
  }, {
    title: "Financial Services & FinTech",
    description: "AML/KYC enablement, secure integrations and compliance-driven digital platforms"
  }, {
    title: "Secure Digital Transformation",
    description: "infrastructure modernization, API security, AI governance and enterprise solutions"
  }];
  return <section data-section-id="223276" id="contact-inquiry" className="bg-background text-foreground relative overflow-hidden py-24 lg:py-32">
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-[20%] -left-[10%] size-[50%] rounded-lg bg-primary/5 blur-[120px]" />
        <div className="absolute top-[60%] -right-[10%] size-[40%] rounded-lg bg-accent/5 blur-[100px]" />
        <div className="absolute inset-0 bg-[url('https://wpvc-images.s3.us-east-1.amazonaws.com/images/1683494/img/fetc_grid_pattern.png')] opacity-[0.03]" />
      </div>

      <div className="container relative z-10 mx-auto px-4 md:px-6">
        <div className="grid gap-12 lg:grid-cols-[1fr_1.2fr] lg:gap-20 xl:gap-24">
          <div className="flex flex-col justify-center space-y-10">
            <div className="space-y-6">
              <div className="inline-flex items-center rounded-md border border-primary/20 bg-primary/10 px-3 py-1 text-sm font-medium text-primary">
                <Zap className="mr-2 size-4" />
                DISCUSS YOUR PRIORITIES
              </div>
              <h2 className="font-default text-4xl font-bold tracking-tight text-foreground sm:text-5xl lg:text-6xl">
                Solutions Aligned to <br className="hidden lg:block" />
                <span className="text-primary">
                  Your Sector Needs
                </span>
              </h2>
              <p className="max-w-xl text-lg leading-relaxed text-muted-foreground font-default">
                Strengthen your cybersecurity governance, secure your digital platforms, modernize your infrastructure, and enable compliant fintech solutions. Our industry experts guide you through sector-specific challenges and opportunities.
              </p>
            </div>

            <Card className="border-white/10 bg-card/40 backdrop-blur-md shadow-md">
              <CardHeader>
                <CardTitle className="font-default text-xl font-semibold text-foreground">
                  TYPICAL DISCUSSION AREAS
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {discussionAreas.map((area, i) => <div key={i} data-index={i} className="flex items-start gap-4">
                      <div className="flex size-10 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                        <ShieldCheck className="size-5" />
                      </div>
                      <div>
                        <p className="font-medium text-foreground font-default">{area.title}</p>
                        <p className="mt-1 text-sm text-muted-foreground leading-relaxed">
                          {area.description}
                        </p>
                      </div>
                    </div>)}
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="relative">
            <div className="absolute -inset-1 rounded-lg bg-gradient-to-b from-primary/10 to-transparent opacity-30 blur-2xl" />
            
            <Card className="relative overflow-hidden border-white/10 bg-card/80 shadow-2xl backdrop-blur-2xl sm:p-2">
              <CardHeader className="pb-8 pt-8 px-6 sm:px-8">
                <CardTitle className="font-default text-2xl text-foreground">
                  Inquiry Form
                </CardTitle>
                <CardDescription className="text-base text-muted-foreground">
                  Submit your details below and our technical consultancy team will reach out to discuss your specific needs.
                </CardDescription>
              </CardHeader>
              <CardContent className="px-6 pb-8 sm:px-8">
                <FormProvider formKey="contact_inquiry_form_industries_experience" formId="contact_inquiry_form_industries_experience" sectionName="Contact & Inquiry" defaultValues={{
                name: "",
                email: "",
                organization: "",
                role: "",
                phone: "",
                partnership_type: [],
                message: ""
              }} submitText="Request a Consultation" successMessage="Thank you for contacting FETC. Our experts will review your inquiry and contact you shortly." errorMessage="There was an issue submitting your inquiry. Please try again or contact us directly at info@fetc.ae.">
                  <ContactForm />
                </FormProvider>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>;
}